import {
  CpuChipIcon,
  WrenchIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  UserIcon,
  PlayIcon,
  StopIcon,
  ExclamationTriangleIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'

interface NodePaletteItem {
  type: string
  label: string
  icon: React.ReactNode
  description: string
  color: string
}

const paletteItems: NodePaletteItem[] = [
  {
    type: 'start',
    label: 'Start',
    icon: <PlayIcon className="w-5 h-5" />,
    description: 'Entry point',
    color: 'bg-green-500/15 text-green-600 border-green-300',
  },
  {
    type: 'end',
    label: 'End',
    icon: <StopIcon className="w-5 h-5" />,
    description: 'Exit point',
    color: 'bg-red-500/15 text-red-600 border-red-300',
  },
  {
    type: 'llm',
    label: 'LLM Call',
    icon: <CpuChipIcon className="w-5 h-5" />,
    description: 'Call language model',
    color: 'bg-blue-500/15 text-blue-600 border-blue-300',
  },
  {
    type: 'agent',
    label: 'Agent',
    icon: <CpuChipIcon className="w-5 h-5" />,
    description: 'Execute an agent',
    color: 'bg-blue-500/15 text-blue-600 border-blue-300',
  },
  {
    type: 'tool',
    label: 'Tool Call',
    icon: <WrenchIcon className="w-5 h-5" />,
    description: 'Execute a tool',
    color: 'bg-purple-500/15 text-purple-600 border-purple-300',
  },
  {
    type: 'conditional',
    label: 'Conditional',
    icon: <QuestionMarkCircleIcon className="w-5 h-5" />,
    description: 'Branch logic',
    color: 'bg-orange-500/15 text-orange-600 border-orange-300',
  },
  {
    type: 'loop',
    label: 'Loop',
    icon: <ArrowPathIcon className="w-5 h-5" />,
    description: 'Iterate over items',
    color: 'bg-orange-500/15 text-orange-600 border-orange-300',
  },
  {
    type: 'humanInput',
    label: 'Human Input',
    icon: <UserIcon className="w-5 h-5" />,
    description: 'Request user input',
    color: 'bg-gray-100 text-gray-700 border-gray-300',
  },
  {
    type: 'tryCatch',
    label: 'Try/Catch',
    icon: <ExclamationTriangleIcon className="w-5 h-5" />,
    description: 'Error handling',
    color: 'bg-red-500/15 text-red-600 border-red-300',
  },
  {
    type: 'retry',
    label: 'Retry',
    icon: <ArrowUturnLeftIcon className="w-5 h-5" />,
    description: 'Retry with backoff',
    color: 'bg-orange-500/15 text-orange-600 border-orange-300',
  },
]

interface NodePaletteProps {
  onDragStart: (event: React.DragEvent, nodeType: string) => void
}

export default function NodePalette({ onDragStart }: NodePaletteProps) {
  return (
    <div className="bg-white border-r border-gray-200 w-64 p-4 overflow-y-auto">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Node Palette</h3>

      <div className="space-y-2">
        {paletteItems.map((item) => (
          <div
            key={item.type}
            draggable
            onDragStart={(e) => onDragStart(e, item.type)}
            className={`
              flex items-start gap-3 p-3 rounded-lg border-2 cursor-grab active:cursor-grabbing
              hover:shadow-md transition-shadow ${item.color}
            `}
          >
            <div className="flex-shrink-0 mt-0.5">{item.icon}</div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium">{item.label}</h4>
              <p className="text-xs opacity-75 mt-0.5">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <h4 className="text-xs font-semibold text-blue-600 mb-2">How to use</h4>
        <ul className="text-xs text-blue-600 space-y-1">
          <li>• Drag nodes onto canvas</li>
          <li>• Connect nodes with edges</li>
          <li>• Click nodes to configure</li>
          <li>• Delete with backspace/delete</li>
        </ul>
      </div>
    </div>
  )
}
